import { stringRoutes } from "@/config/global.strings";
import { today, YYYYMMDD } from "@/helpers/dateHelper";
import { DirectionEnum } from "@/models/enums";
import { AvailableDate, CalendarConfig } from "@/models/www/CalendarConfig";
import calendarService from "@/services/calendar.service";
import { useBookingStore } from "@/store/booking";
import { computed, onMounted, readonly, ref, watch } from "vue";

const calendarConfigOutward = ref<CalendarConfig>({ maxBookableDate: new Date(), availableDates: [], calendarNotes: [], route: '' });
const calendarConfigReturn = ref<CalendarConfig>({ maxBookableDate: new Date(), availableDates: [], calendarNotes: [], route: '' });
const loading = ref<boolean>(false);
const initializing = ref(false);
const useBookingCalendar = (init = false) => {
    const { getDate, getRoute, getIsLocked, setDate, getBooking } = useBookingStore();

    const saveCalendarData = (data: CalendarConfig, direction: DirectionEnum) => {
        if (direction === DirectionEnum.Outward) {
            calendarConfigOutward.value = data;
        }
        else {
            calendarConfigReturn.value = data;
        }
    }

    const fetchCalendarConfig = (direction: DirectionEnum, startDate?: string): Promise<void> => {
        const route = getRoute(direction);

        return new Promise((resolve, reject) => {
            //check that there is a valid route in store
            if (Object.keys(stringRoutes).includes(route)) {
                calendarService.getCalendarSettings(route, startDate).then(resp => {
                    saveCalendarData(resp, direction);
                    resolve();
                }).catch(err => reject(err));
            } else {
                resolve();
            }
        })

    }

    const getAvailableDates = (direction: DirectionEnum) => {
        const route = getRoute(direction);

        let available = [] as AvailableDate[];

        if (getIsLocked(direction)) {
            const date = getDate(direction);
            if (date) {
                available = [{ start: new Date(date), end: new Date(date) }]
            }
        }
        else {
            if (direction === DirectionEnum.Outward) {
                available = (calendarConfigOutward.value.availableDates.find(
                    (d) => d.route.code === route
                )?.dates ?? [{ start: new Date(0), end: new Date(0) }]) as AvailableDate[];
            }
            else {
                available = (calendarConfigReturn.value.availableDates.find(
                    (d) => d.route.code === route
                )?.dates ?? [{ start: new Date(0), end: new Date(0) }]) as AvailableDate[];
            }


            if (direction === DirectionEnum.Return && available.length > 1) {
                const date = getDate(DirectionEnum.Outward);
                //Filter out the dates that are before the outward date
                available = available.filter(
                    (a) => {
                        return YYYYMMDD(a.start) >= YYYYMMDD(date ?? new Date())
                    }
                );
            }

            if (available.length < 1) {
                //If we supply no dates at all, everything will be available..
                available = [{ start: new Date(0), end: new Date(0) }]
            }
        }

        return available.sort((a, b) => { return (a.start as any) - (b.start as any) });
    }

    const getNextAvailableDate = (direction: DirectionEnum) => {
        const startOfToday = today();
        const allDates = getAvailableDates(direction).filter(d => YYYYMMDD(d.start) >= YYYYMMDD(startOfToday));
        return allDates.length > 0 ? allDates[0] : undefined;
    }

    const availableDatesOutward = computed(() =>
        getAvailableDates(DirectionEnum.Outward)
    );
    const availableDatesReturn = computed(() =>
        getAvailableDates(DirectionEnum.Return)
    );

    const outwardRoute = computed(() => {
        return getRoute(DirectionEnum.Outward);
    });

    const returnRoute = computed(() => {
        return getRoute(DirectionEnum.Return);
    });

    const startWatch = () => {
        watch(outwardRoute, () => {
            loading.value = true;
            fetchCalendarConfig(DirectionEnum.Outward)
                .then(() => setDefaultDate(DirectionEnum.Outward))
                .finally(() => (loading.value = false));
        });

        watch(returnRoute, () => {
            loading.value = true;
            fetchCalendarConfig(DirectionEnum.Return)
                .then(() => setDefaultDate(DirectionEnum.Return))
                .finally(() => (loading.value = false));
        });
    }

    const dateIsAvailable = (date: Date, direction: DirectionEnum) => {
        if (direction === DirectionEnum.Outward) {
            return (
                typeof availableDatesOutward.value?.find(
                    (v) => YYYYMMDD(v.start) === YYYYMMDD(date)
                ) !== "undefined"
            );
        } else {
            return (
                typeof availableDatesReturn.value?.find(
                    (v) => YYYYMMDD(v.start) === YYYYMMDD(date)
                ) !== "undefined"
            );
        }
    };

    const setDefaultDate = (direction: DirectionEnum) => {
        const selectedDate = getDate(direction);

        if (getAvailableDates(direction)) {
            if (!selectedDate || !dateIsAvailable(selectedDate, direction)) {
                //Selected date is not available
                const nextAvailable = getNextAvailableDate(direction)?.start;
                setDate(direction, nextAvailable);
            }
        }
    };

    const hasOutwardDate = () => {
        if (!getBooking.outward.date) {
            return false;
        }

        return true;
    };

    const hasReturnDate = () => {
        if (
            getBooking.tripType === DirectionEnum.Return &&
            !getBooking.return.date
        ) {
            return false;
        }

        return true;
    };

    if (init) {
        startWatch();
        onMounted(() => {
            const outwardRoute = getRoute(DirectionEnum.Outward);
            const returnRoute = getRoute(DirectionEnum.Return);

            const promises = [];

            if (calendarConfigOutward.value.route !== outwardRoute) {
                promises.push(fetchCalendarConfig(DirectionEnum.Outward));
            }

            if (calendarConfigReturn.value.route !== returnRoute) {
                promises.push(fetchCalendarConfig(DirectionEnum.Return));
            }

            if (promises.length > 0) {
                initializing.value = true;
                Promise.all(promises).then(() => {
                    setDefaultDate(DirectionEnum.Outward);
                    setDefaultDate(DirectionEnum.Return);
                }).finally(() => initializing.value = false);
            }
        });
    }

    return {
        calendarConfigOutward: readonly(calendarConfigOutward),
        calendarConfigReturn: readonly(calendarConfigReturn),
        fetchCalendarConfig,
        getNextAvailableDate,
        getAvailableDates,
        availableDatesOutward,
        availableDatesReturn,
        hasReturnDate,
        hasOutwardDate,
        setDefaultDate,
        loading,
        initializing
    }
}

export default useBookingCalendar;